import { useState } from "react";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Menu, Save, Plus, Edit, Trash2, Users, Loader2 } from "lucide-react";
import { getAllTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember, TeamMember } from "@/api/teamService";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ImageUpload } from "@/components/ui/image-upload";

const TeamManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", position: "", description: "", image_url: "", hover_image_url: "",
    email: "", linkedin_url: "", phone: "", sort_order: 1, is_active: true,
  });

  const { data: teamMembers = [], isLoading } = useQuery('teamMembers', getAllTeamMembers, { staleTime: 300000, refetchOnWindowFocus: false });

  const createMutation = useMutation(createTeamMember, {
    onSuccess: () => { queryClient.invalidateQueries('teamMembers'); toast({ title: "Teamlid toegevoegd" }); setIsDialogOpen(false); resetForm(); },
    onError: (error: any) => { toast({ title: "Fout", description: error.message, variant: "destructive" }); },
  });

  const updateMutation = useMutation(
    ({ id, updates }: { id: string; updates: Partial<TeamMember> }) => updateTeamMember(id, updates),
    {
      onSuccess: () => { queryClient.invalidateQueries('teamMembers'); toast({ title: "Bijgewerkt" }); setIsDialogOpen(false); resetForm(); },
      onError: (error: any) => { toast({ title: "Fout", description: error.message, variant: "destructive" }); },
    }
  );

  const deleteMutation = useMutation(deleteTeamMember, {
    onSuccess: () => { queryClient.invalidateQueries('teamMembers'); toast({ title: "Verwijderd" }); },
    onError: (error: any) => { toast({ title: "Fout", description: error.message, variant: "destructive" }); },
  });

  const handleSaveMember = async () => {
    if (editingMember) {
      await updateMutation.mutateAsync({ id: editingMember.id, updates: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
  };

  const handleDeleteMember = async (id: string) => {
    if (confirm("Teamlid verwijderen?")) await deleteMutation.mutateAsync(id);
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({ name: member.name, position: member.position, description: member.description || "", image_url: member.image_url || "", hover_image_url: member.hover_image_url || "", email: member.email || "", linkedin_url: member.linkedin_url || "", phone: member.phone || "", sort_order: member.sort_order, is_active: member.is_active });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: "", position: "", description: "", image_url: "", hover_image_url: "", email: "", linkedin_url: "", phone: "", sort_order: teamMembers.length + 1, is_active: true });
    setEditingMember(null);
  };

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const Field = ({ label, id, type = 'text', value, onChange, placeholder }: { label: string; id: string; type?: string; value: string | number; onChange: (v: string) => void; placeholder?: string }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-[#202223] mb-1">{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300 transition-colors" />
    </div>
  );

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto bg-[#f6f6f7]">
          <div className="border-b border-gray-200 bg-white px-8 py-6">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="md:hidden"><Menu className="h-5 w-5" /></SidebarTrigger>
                <div>
                  <h1 className="text-xl font-semibold text-[#202223]">Team</h1>
                  <p className="text-sm text-[#6d7175] mt-0.5">Beheer teamleden voor de contactpagina</p>
                </div>
              </div>
              <button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="inline-flex items-center gap-2 bg-[#202223] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors">
                <Plus className="h-4 w-4" /> Nieuw teamlid
              </button>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="max-w-5xl mx-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : teamMembers.length === 0 ? (
                <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 p-12 text-center">
                  <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-[#6d7175] mb-4">Nog geen teamleden</p>
                  <button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="inline-flex items-center gap-2 bg-[#202223] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333]">
                    <Plus className="h-4 w-4" /> Eerste teamlid toevoegen
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-200/60 divide-y divide-gray-100">
                  {teamMembers.sort((a, b) => a.sort_order - b.sort_order).map((member) => (
                    <div key={member.id} className="flex items-center gap-4 px-5 py-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                        {member.image_url ? (
                          <img src={member.image_url} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Users className="h-5 w-5 text-gray-400" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#202223]">{member.name}</p>
                          {!member.is_active && <span className="text-[11px] bg-gray-100 text-[#6d7175] px-1.5 py-0.5 rounded">Inactief</span>}
                        </div>
                        <p className="text-[13px] text-[#6d7175]">{member.position}</p>
                        {member.email && <p className="text-xs text-[#6d7175] mt-0.5">{member.email}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleEditMember(member)} className="p-2 rounded-lg text-gray-400 hover:text-[#202223] hover:bg-gray-50"><Edit className="h-4 w-4" /></button>
                        <button onClick={() => handleDeleteMember(member.id)} disabled={deleteMutation.isLoading} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-xl bg-white rounded-xl border-0 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-[#202223]">
                {editingMember ? "Teamlid bewerken" : "Nieuw teamlid"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Naam" id="name" value={formData.name} onChange={(v) => setFormData({ ...formData, name: v })} placeholder="Volledige naam" />
                <Field label="Functie" id="position" value={formData.position} onChange={(v) => setFormData({ ...formData, position: v })} placeholder="Functietitel" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202223] mb-1">Beschrijving</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Korte beschrijving" rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-300 resize-y" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ImageUpload label="Hoofdfoto" value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url || "" })} disabled={isSaving} />
                <ImageUpload label="Hover foto" value={formData.hover_image_url} onChange={(url) => setFormData({ ...formData, hover_image_url: url || "" })} disabled={isSaving} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="E-mail" id="email" type="email" value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} placeholder="naam@vermado.nl" />
                <Field label="Telefoon" id="phone" value={formData.phone} onChange={(v) => setFormData({ ...formData, phone: v })} placeholder="+31 6 ..." />
              </div>
              <Field label="LinkedIn URL" id="linkedin_url" value={formData.linkedin_url} onChange={(v) => setFormData({ ...formData, linkedin_url: v })} placeholder="https://linkedin.com/in/..." />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Volgorde" id="sort_order" type="number" value={formData.sort_order} onChange={(v) => setFormData({ ...formData, sort_order: parseInt(v) || 1 })} />
                <div className="flex items-center gap-2 pt-7">
                  <button
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${formData.is_active ? 'bg-[#202223]' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${formData.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-[#202223]">Actief</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setIsDialogOpen(false)} className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#6d7175] hover:text-[#202223] border border-gray-200 hover:bg-gray-50 transition-colors">Annuleren</button>
              <button onClick={handleSaveMember} disabled={isSaving || !formData.name || !formData.position} className="inline-flex items-center gap-2 bg-[#202223] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] disabled:opacity-50 transition-colors">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Opslaan
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SidebarProvider>
  );
};

export default TeamManagement;
